/**
 * KONFIGURACJA AMBIENT CRITTERS — dekoracyjne stworzonka tła per-biom.
 *
 * Czwarty system wizualny „ożywiający" planszę (po ambientFx/atmosphere/parallax).
 * Stworzonka NIE mają roli w gameplayu — to czysta scenografia: motyle nad
 * kwiatami, ptak przelatujący w tle, biedronka pełzająca po trawie.
 *
 * **Model:** `AmbientCrittersSystem` trzyma per-typ scheduler. Co jakiś czas
 * (`spawnIntervalMs`) spawnuje stworzonko, jeśli nie przekroczono `maxConcurrent`.
 * Każde stworzonko porusza się po ścieżce zależnej od `behavior`, a po wyjściu z
 * kadru (albo upływie życia) jest niszczone. Ścieżka liczona przy spawnie z
 * aktualnego `cam.worldView`.
 *
 * **Asset:** jedno stworzonko = jeden transparent sticker PNG (jak pet). Animację
 * RUCHU robi kod (ścieżka + sin-bob + opcjonalna rotacja), więc NIE potrzeba
 * sprite-sheetów ani klatek.
 *
 * **Fail-soft:** brak tekstury typu → system pomija typ z `warn` (gra działa).
 *
 * **Performance:** kilka sprite'ów naraz (cap `maxConcurrent`), transient.
 * Pomijalny koszt.
 */

import type { CrittersKey } from '@/domain/types/critters';
export type { CrittersKey };

/**
 * Wzorzec ruchu stworzonka:
 *  - `flutter` — meandruje po lokalnym obszarze z sin-bobem, żyje przez
 *    `lifetimeMs`, potem znika (fade). Motyl, biedronka, glitch-stworek.
 *  - `cross`   — wlatuje z jednej krawędzi ekranu, leci prosto na drugą (z
 *    sin-bobem + opcjonalną rotacją), znika za krawędzią. Ptak, tumbleweed
 *    (z `rotateSpeed`), zając, bandyta na speederze.
 *  - `soar`    — krąży po elipsie nad kadrem `loops` razy, potem odlatuje.
 *    Ptak drapieżny w górach.
 */
export type CritterBehavior = 'flutter' | 'cross' | 'soar';

export interface CritterTypeConfig {
  /** Tekstura PNG (transparent sticker, folder `critters/`). Brak → typ pominięty. */
  textureKey: string;
  /** Wzorzec ruchu. */
  behavior: CritterBehavior;
  /** Zakres skali sprite'a (px docelowe liczone z tekstury, jak StallPet). */
  scale: { min: number; max: number };
  /** Prędkość ruchu (px/s), losowana per spawn. */
  speed: { min: number; max: number };
  /** Z-ordering. Konwencja: dest=0, pet=5, gracz=6, coins=10. Stworzonka:
   *  nisko (biedronka ~4), przy graczu (motyl ~7), wysoko nad sceną (ptak ~40). */
  depth: number;
  /** Odstęp między spawnami TEGO typu (ms), losowany w zakresie. */
  spawnIntervalMs: { min: number; max: number };
  /** Maks. ile sztuk tego typu naraz na scenie. */
  maxConcurrent: number;
  /** Amplituda + częstotliwość pionowego „trzepotania" (sin). Brak = lot płaski. */
  bob?: { amplitudePx: number; freqHz: number };
  /** Prędkość obrotu (deg/s) — „toczenie się" (tumbleweed). Brak = bez obrotu. */
  rotateSpeed?: number;
  /** Pionowy pas viewportu (0=góra, 1=dół), w którym żyje stworzonko. Np. ptak
   *  wysoko {0.05,0.3}, biedronka nisko {0.6,0.95}. Domyślnie cały kadr. */
  bandY?: { min: number; max: number };
  /** Liczba okrążeń dla `soar`. */
  loops?: number;
  /** Czas życia dla `flutter` (ms), losowany ±25%. Domyślnie ~6000. */
  lifetimeMs?: number;
  /** Odbij sprite poziomo zależnie od kierunku ruchu (np. ptak patrzy dokąd leci). */
  flipToFaceDir?: boolean;
  /**
   * Reakcja na gracza (tylko `flutter`): stworzonko ucieka, gdy gracz wejdzie w
   * `radiusPx`, i wraca gdy się oddali. Bez tego pola — ignoruje gracza.
   *  - `radiusPx`   — promień „spłoszenia" (px świata).
   *  - `speedMult`  — ile razy szybciej ucieka niż normalnie meandruje.
   *  - `curious`    — po ucieczce podpełza z powrotem w stronę gracza (motyl
   *                   „zaciekawiony"); bez tego po prostu wraca do meandrowania.
   */
  flee?: { radiusPx: number; speedMult: number; curious?: boolean };
}

export interface CrittersPreset {
  types: CritterTypeConfig[];
}

// ─────────────────────────────────────────────────────────────────────────────

export const CRITTERS_PRESETS: Record<CrittersKey, CrittersPreset> = {
  // ─── MEADOW — motyle, ptak, biedronka ───────────────────────────────────────
  // Prototyp. Motyle trzepoczą nad kwiatami (mid, nad graczem), ptak przelatuje
  // wysoko nad sceną od czasu do czasu, biedronka wolno pełza nisko nad trawą.
  meadow_critters: {
    types: [
      {
        textureKey: 'critter_meadow_butterfly',
        behavior: 'flutter',
        scale: { min: 22, max: 30 },
        speed: { min: 30, max: 55 },
        depth: 7,                              // nad graczem (6) — fruwa w powietrzu
        spawnIntervalMs: { min: 2500, max: 6000 },
        maxConcurrent: 3,
        bob: { amplitudePx: 10, freqHz: 1.6 }, // wyraźne trzepotanie
        bandY: { min: 0.15, max: 0.7 },
        lifetimeMs: 7000,
        // Motyl ucieka gdy gracz blisko, potem ciekawsko wraca.
        flee: { radiusPx: 95, speedMult: 2.4, curious: true },
      },
      {
        textureKey: 'critter_meadow_bird',
        behavior: 'cross',
        scale: { min: 26, max: 34 },
        speed: { min: 90, max: 140 },
        depth: 40,                             // przelot WYSOKO nad całą sceną
        spawnIntervalMs: { min: 9000, max: 18000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 6, freqHz: 0.8 },
        bandY: { min: 0.05, max: 0.3 },
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_meadow_ladybug',
        behavior: 'flutter',
        scale: { min: 14, max: 18 },
        speed: { min: 14, max: 26 },
        depth: 4,                              // nisko nad ziemią (pod petami 5)
        spawnIntervalMs: { min: 6000, max: 12000 },
        maxConcurrent: 2,
        // bez bob — pełza, nie lata
        bandY: { min: 0.55, max: 0.95 },
        lifetimeMs: 9000,
        // Biedronka szybko czmycha gdy gracz nadepnie blisko (bez powrotu).
        flee: { radiusPx: 55, speedMult: 2.0, curious: false },
      },
    ],
  },

  // ─── MOUNTAIN — orzeł krążący wysoko + kozica nisko przy ziemi ─────────────
  mountain_critters: {
    types: [
      {
        textureKey: 'critter_mountain_golden_eagle',
        behavior: 'soar',
        scale: { min: 26, max: 36 },
        speed: { min: 70, max: 105 },
        depth: 42,
        spawnIntervalMs: { min: 11000, max: 22000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 4, freqHz: 0.45 },
        bandY: { min: 0.05, max: 0.28 },
        loops: 2,
      },
      {
        textureKey: 'critter_mountain_chamois',
        behavior: 'cross',
        scale: { min: 22, max: 30 },
        speed: { min: 90, max: 140 },
        depth: 4,
        spawnIntervalMs: { min: 7000, max: 14000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 3, freqHz: 1.15 },
        bandY: { min: 0.62, max: 0.94 },
      },
    ],
  },

  // ─── TATOOINE — tumbleweed + szybki pustynny rider ─────────────────────────
  starwars_critters: {
    types: [
      {
        textureKey: 'critter_starwars_tumbleweed',
        behavior: 'cross',
        scale: { min: 18, max: 26 },
        speed: { min: 80, max: 130 },
        depth: 4,
        spawnIntervalMs: { min: 4000, max: 9000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 2, freqHz: 0.7 },
        rotateSpeed: 180,
        bandY: { min: 0.64, max: 0.95 },
      },
      {
        textureKey: 'critter_starwars_speeder_bandit',
        behavior: 'cross',
        scale: { min: 28, max: 38 },
        speed: { min: 180, max: 260 },
        depth: 35,
        spawnIntervalMs: { min: 14000, max: 26000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 4, freqHz: 1.1 },
        bandY: { min: 0.25, max: 0.55 },
        flipToFaceDir: true,
      },
    ],
  },

  // ─── PIXEL WORLD — mały glitch-duch + ptak 8-bit ───────────────────────────
  pixel_critters: {
    types: [
      {
        textureKey: 'critter_pixel_glitch_ghost',
        behavior: 'flutter',
        scale: { min: 20, max: 28 },
        speed: { min: 35, max: 65 },
        depth: 8,
        spawnIntervalMs: { min: 3000, max: 7000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 8, freqHz: 1.8 },
        bandY: { min: 0.2, max: 0.75 },
        lifetimeMs: 6500,
        flee: { radiusPx: 80, speedMult: 2.2, curious: true },
      },
      {
        textureKey: 'critter_pixel_bird',
        behavior: 'cross',
        scale: { min: 22, max: 30 },
        speed: { min: 95, max: 150 },
        depth: 38,
        spawnIntervalMs: { min: 8000, max: 16000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 5, freqHz: 1.0 },
        bandY: { min: 0.08, max: 0.34 },
        flipToFaceDir: true,
      },
    ],
  },

  // ─── OCEAN — drobne rybki i meduza dryfujące w głębinach ───────────────────
  ocean_critters: {
    types: [
      {
        textureKey: 'critter_ocean_clownfish',
        behavior: 'cross',
        scale: { min: 20, max: 28 },
        speed: { min: 70, max: 115 },
        depth: 6,
        spawnIntervalMs: { min: 4500, max: 9500 },
        maxConcurrent: 2,
        bob: { amplitudePx: 5, freqHz: 0.9 },
        bandY: { min: 0.22, max: 0.78 },
      },
      {
        textureKey: 'critter_ocean_jelly_pup',
        behavior: 'flutter',
        scale: { min: 22, max: 32 },
        speed: { min: 18, max: 34 },
        depth: 8,
        spawnIntervalMs: { min: 6500, max: 13000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 12, freqHz: 0.55 },
        bandY: { min: 0.12, max: 0.72 },
        lifetimeMs: 10000,
      },
      {
        textureKey: 'critter_ocean_seahorse',
        behavior: 'flutter',
        scale: { min: 18, max: 26 },
        speed: { min: 14, max: 28 },
        depth: 7,
        spawnIntervalMs: { min: 9000, max: 17000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 8, freqHz: 0.65 },
        bandY: { min: 0.28, max: 0.86 },
        lifetimeMs: 11000,
      },
    ],
  },

  // ─── FOOD — skacząca frytka przebiegająca blat + pszczółka lecąca do słodkości ─
  // Frytka „biegnie" w poprzek kadru (cross) z energicznym podskokiem; pszczółka
  // meandruje nad jedzeniem (flutter), spłoszona ucieka przed graczem i ciekawsko
  // wraca. Lekki, wesoły akcent pasujący do baru szybkiej obsługi.
  food_critters: {
    types: [
      {
        textureKey: 'critter_food_fry',
        behavior: 'cross',
        scale: { min: 22, max: 30 },
        speed: { min: 95, max: 150 },
        depth: 6,
        spawnIntervalMs: { min: 5000, max: 10000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 11, freqHz: 2.2 },   // energiczne podskoki — „bieg" frytki
        bandY: { min: 0.35, max: 0.9 },
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_food_bee',
        behavior: 'flutter',
        scale: { min: 18, max: 26 },
        speed: { min: 34, max: 60 },
        depth: 7,                                  // nad graczem — lata w powietrzu
        spawnIntervalMs: { min: 4000, max: 8500 },
        maxConcurrent: 2,
        bob: { amplitudePx: 9, freqHz: 1.7 },
        bandY: { min: 0.18, max: 0.72 },
        lifetimeMs: 7500,
        flee: { radiusPx: 90, speedMult: 2.3, curious: true },
      },
    ],
  },

  // ── ATLANTIS (biom 7) — ławica rybek (cross) + meduza (flutter) + delfin (ATL-1, górą) ──
  atlantis_critters: {
    types: [
      {
        textureKey: 'critter_atlantis_fish',
        behavior: 'cross',
        scale: { min: 20, max: 28 },
        speed: { min: 90, max: 145 },
        depth: 6,
        spawnIntervalMs: { min: 4000, max: 8000 },
        maxConcurrent: 3,
        bob: { amplitudePx: 8, freqHz: 2.0 },
        bandY: { min: 0.3, max: 0.85 },
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_atlantis_jelly',
        behavior: 'flutter',
        scale: { min: 18, max: 26 },
        speed: { min: 28, max: 52 },
        depth: 7,
        spawnIntervalMs: { min: 5000, max: 9500 },
        maxConcurrent: 2,
        bob: { amplitudePx: 10, freqHz: 1.4 },
        bandY: { min: 0.18, max: 0.7 },
        lifetimeMs: 8000,
        flee: { radiusPx: 85, speedMult: 2.1, curious: true },
      },
      {
        textureKey: 'critter_atlantis_dolphin',
        behavior: 'cross',
        scale: { min: 34, max: 46 },
        speed: { min: 140, max: 210 },
        depth: 8,
        spawnIntervalMs: { min: 8000, max: 15000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 16, freqHz: 1.1 },
        bandY: { min: 0.12, max: 0.45 },
        flipToFaceDir: true,
      },
    ],
  },

  // ─── BEACH (event Wakacje Capibar) — mewy nad morzem + kraby na piasku ──────
  beach_critters: {
    types: [
      {
        textureKey: 'critter_beach_seagull',
        behavior: 'cross',
        scale: { min: 22, max: 30 },
        speed: { min: 80, max: 130 },
        depth: 8,
        spawnIntervalMs: { min: 4000, max: 8000 },
        maxConcurrent: 3,
        bob: { amplitudePx: 10, freqHz: 1.6 },
        bandY: { min: 0.05, max: 0.3 },   // górne pasmo — niebo nad morzem
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_beach_crab',
        behavior: 'cross',
        scale: { min: 16, max: 22 },
        speed: { min: 30, max: 55 },
        depth: 6,
        spawnIntervalMs: { min: 5000, max: 9500 },
        maxConcurrent: 2,
        bob: { amplitudePx: 3, freqHz: 3.0 },
        bandY: { min: 0.7, max: 0.92 },   // dolne pasmo — piasek
        flipToFaceDir: true,
      },
    ],
  },

  // ─── SCHOOL (biom 8) — samolocik + kleksik + książkowy robaczek ────────────
  school_critters: {
    types: [
      {
        // Rzadki akcent wysoko nad ławką — przelot z krawędzi na krawędź.
        // depth 40 = nad wszystkim w świecie (HUD to osobna warstwa DOM).
        textureKey: 'critter_school_paper_plane',
        behavior: 'cross',
        scale: { min: 30, max: 42 },
        speed: { min: 150, max: 220 },
        depth: 40,
        spawnIntervalMs: { min: 8000, max: 15000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 12, freqHz: 0.9 },
        bandY: { min: 0.08, max: 0.4 },
        flipToFaceDir: true,
      },
      {
        // Ciekawski — ucieka przed graczem i podpełza z powrotem (jak motyl z Łąki).
        textureKey: 'critter_school_ink_sprite',
        behavior: 'flutter',
        scale: { min: 18, max: 26 },
        speed: { min: 30, max: 55 },
        depth: 7,
        spawnIntervalMs: { min: 3000, max: 6500 },
        maxConcurrent: 2,
        bob: { amplitudePx: 8, freqHz: 1.5 },
        bandY: { min: 0.2, max: 0.75 },
        lifetimeMs: 8000,
        flee: { radiusPx: 90, speedMult: 2.3, curious: true },
      },
      {
        // Czmycha bez powrotu (wzorzec biedronki) — drobiazg nisko, pod petami.
        textureKey: 'critter_school_bookworm',
        behavior: 'flutter',
        scale: { min: 14, max: 20 },
        speed: { min: 22, max: 38 },
        depth: 4,
        spawnIntervalMs: { min: 4500, max: 9000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 4, freqHz: 2.2 },
        bandY: { min: 0.55, max: 0.95 },
        lifetimeMs: 7000,
        flee: { radiusPx: 55, speedMult: 2.0 },
      },
    ],
  },

  // ─── ARCTIC (biom 9) — sowa wysoko, foczka i lisek na krze ───────────────
  arctic_critters: {
    types: [
      {
        textureKey: 'critter_arctic_snowy_owl',
        behavior: 'soar',
        scale: { min: 27, max: 38 },
        speed: { min: 75, max: 110 },
        depth: 42,
        spawnIntervalMs: { min: 10000, max: 19000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 5, freqHz: 0.5 },
        bandY: { min: 0.05, max: 0.3 },
        loops: 2,
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_arctic_seal_pup',
        behavior: 'cross',
        scale: { min: 20, max: 28 },
        speed: { min: 55, max: 90 },
        depth: 4,
        spawnIntervalMs: { min: 6500, max: 12000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 3, freqHz: 1.7 },
        bandY: { min: 0.62, max: 0.94 },
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_arctic_fox_cub',
        behavior: 'flutter',
        scale: { min: 18, max: 25 },
        speed: { min: 28, max: 48 },
        depth: 7,
        spawnIntervalMs: { min: 4500, max: 9000 },
        maxConcurrent: 2,
        bob: { amplitudePx: 5, freqHz: 1.35 },
        bandY: { min: 0.3, max: 0.82 },
        lifetimeMs: 8000,
        flee: { radiusPx: 85, speedMult: 2.2, curious: true },
      },
    ],
  },
  // ─── FOREST (Puszcza Niepołomicka) — motyl leśny, ptaszek, biedronka ────────
  // Trzy archetypy z DESIGN_ADD_NEW_BIOME.md §10. Uwaga: to DEKORACJE, nie pety —
  // jelenie/sarny są petami i nie mogą tu latać jako tło (dziecko by się gubiło,
  // co można złapać). Dlatego drobiazgi: motyl, ptaszek, biedronka.
  forest_critters: {
    types: [
      {
        textureKey: 'critter_puszcza_butterfly',
        behavior: 'flutter',
        scale: { min: 20, max: 28 },
        speed: { min: 26, max: 48 },
        depth: 7,                              // nad graczem — fruwa w powietrzu
        spawnIntervalMs: { min: 3000, max: 7000 },
        maxConcurrent: 3,
        bob: { amplitudePx: 9, freqHz: 1.5 },
        bandY: { min: 0.2, max: 0.75 },
        lifetimeMs: 7500,
        flee: { radiusPx: 90, speedMult: 2.3, curious: true },
      },
      {
        textureKey: 'critter_puszcza_bird',
        behavior: 'cross',
        scale: { min: 24, max: 32 },
        speed: { min: 95, max: 145 },
        depth: 40,                             // przelot WYSOKO nad koronami
        spawnIntervalMs: { min: 10_000, max: 19_000 },
        maxConcurrent: 1,
        bob: { amplitudePx: 5, freqHz: 0.9 },
        bandY: { min: 0.05, max: 0.28 },
        flipToFaceDir: true,
      },
      {
        textureKey: 'critter_puszcza_beetle',
        behavior: 'flutter',
        scale: { min: 13, max: 18 },
        speed: { min: 12, max: 24 },
        depth: 4,                              // nisko na ściółce (pod petami)
        spawnIntervalMs: { min: 6500, max: 13_000 },
        maxConcurrent: 2,
        // bez bob — pełza po ściółce
        bandY: { min: 0.55, max: 0.95 },
        lifetimeMs: 9500,
        flee: { radiusPx: 55, speedMult: 2.0, curious: false },
      },
    ],
  },
};
